import config from "@/config"

import type { Locale } from "./i18n/locale"

/**
 * One switch for the public site (T1310, Owner 2026-11-02): while `coming-soon`, purchase and sign-in
 * are closed, every sign-up call to action leads to the waitlist, the site is `noindex` and the
 * marketing host serves no app or API route. Flip to `live` with `VITE_LAUNCH_STAGE=live` and a rebuild.
 */
export type LaunchStage = "coming-soon" | "live"

/**
 * Production builds default to `coming-soon` so a forgotten variable can never open sign-in; dev and
 * tests default to `live`. Any other value fails loudly in production.
 */
export function resolveLaunchStage(value: string | undefined, prod = false): LaunchStage {
  const v = value?.trim()
  if (v === "live" || v === "coming-soon") return v
  if (v && prod)
    throw new Error(`VITE_LAUNCH_STAGE must be "coming-soon" or "live", got "${value}"`)
  return prod ? "coming-soon" : "live"
}

export const LAUNCH_STAGE: LaunchStage = resolveLaunchStage(
  import.meta.env.VITE_LAUNCH_STAGE,
  import.meta.env.PROD,
)

export const WAITLIST_PATH = "/waitlist"

export const isComingSoon = (stage: LaunchStage = LAUNCH_STAGE): boolean => stage === "coming-soon"

const WAITLIST_LABEL: Record<Locale, string> = { en: "Join the waitlist", zh: "加入候补名单" }

/** A call to action that points at sign-in leads to the waitlist while the site is coming soon. */
export function gateCta(
  cta: { href: string; label: string },
  locale: Locale,
  stage: LaunchStage = LAUNCH_STAGE,
): { href: string; label: string } {
  if (stage === "live" || !/^\/login(?:[?#]|$)/.test(cta.href)) return cta
  return { href: WAITLIST_PATH, label: WAITLIST_LABEL[locale] }
}

/** Routes of the signed-in app and the API: not served on the marketing host while coming soon. */
const APP_PATH_PREFIXES = [
  "/login",
  "/auth",
  "/home",
  "/review",
  "/trips",
  "/expenses",
  "/settings",
  "/invoices",
  "/business",
  "/customers",
  "/reports",
  "/logbook",
  "/vehicles",
  "/account",
  "/team",
  "/billing",
  "/emails",
  "/api",
]

/** Decoded, lower-cased path with repeated slashes and backslashes collapsed, so `/Login` and `//login` match. */
function normalisePath(pathname: string): string {
  let p = pathname
  try {
    p = decodeURIComponent(p)
  } catch {
    // malformed escape: judge the raw path
  }
  p = p.toLowerCase().replace(/[\\/]+/g, "/")
  // resolve dot segments the URL parser keeps when they arrive encoded
  const out: string[] = []
  for (const seg of p.split("/")) {
    if (seg === "..") out.pop()
    else if (seg !== "." && seg !== "") out.push(seg)
  }
  return `/${out.join("/")}`
}

export function isAppPath(pathname: string): boolean {
  const path = normalisePath(pathname)
  // Marketing pages send CSP violation reports here.
  if (path === "/api/csp-report") return false
  return APP_PATH_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))
}

const onMarketingHost = (url: URL): boolean => url.host === new URL(config.core.websiteUrl).host

/** `www.<marketing host>` redirects to the marketing host (D-DOMAIN-RULES: no www), same path and query. */
export function wwwRedirect(request: Request): Response | null {
  const url = new URL(request.url)
  const marketing = new URL(config.core.websiteUrl)
  if (url.host !== `www.${marketing.host}`) return null
  // Concatenate onto the fixed origin: `new URL("//evil.com", base)` would leave it (open redirect).
  const path = url.pathname.replace(/^[\\/]+/, "")
  return Response.redirect(`${marketing.origin}/${path}${url.search}`, 301)
}

/** True when the request must get a 404: marketing host, app or API path, site still coming soon. */
export function isBlockedWhileComingSoon(
  request: Request,
  stage: LaunchStage = LAUNCH_STAGE,
): boolean {
  const url = new URL(request.url)
  return isComingSoon(stage) && onMarketingHost(url) && isAppPath(url.pathname)
}

/**
 * Server functions the marketing pages call: the waitlist and newsletter forms, the contact form and
 * the signed-in check. Every other server function (sign-in, checkout, app data) is closed on the
 * marketing host while coming soon (T1310).
 */
export const MARKETING_SERVER_FN_FILES = [
  /(^|\/)newsletter\/rpc\/[^/]+\.ts$/,
  /(^|\/)contact\/rpc\/send\.ts$/,
  /(^|\/)auth\/rpc\/get-user\.ts$/,
  // the forms fetch the Turnstile site key here
  /(^|\/)turnstile\/rpc\/get-public-key\.ts$/,
]

export function isServerFnBlocked(
  request: Request,
  filename: string,
  stage: LaunchStage = LAUNCH_STAGE,
): boolean {
  if (!isComingSoon(stage) || !onMarketingHost(new URL(request.url))) return false
  if (filename.includes("..")) return true
  return !MARKETING_SERVER_FN_FILES.some((re) => re.test(filename))
}
